import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ComplianceProgressBar } from "../../components/ComplianceProgressBar/ComplianceProgressBar";
import { getImage } from "../../pages/Organization/InfraManagement/utils/utils";

const InfrastructureCard = ({ integration, systemData, onClick, logoPath }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  if (!systemData) {
    return null;
  }

  const checksSummary = systemData?.checksSummary || {};

  // Transform system data to match ComplianceProgressBar format
  const complianceMetadata = {
    totalRecords: systemData?.totalChecksApplicable || 0,
    totalPassRecords: checksSummary.passingChecks || 0,
    totalFailRecords: checksSummary.failingChecks || 0,
    totalDisabledRecords: checksSummary.disabledChecks || 0,
    totalPendingRecords: checksSummary.pendingChecks || 0,
  };

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: "100%",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        borderRadius: "12px",
        transition: "all 0.3s ease",
        backgroundColor: isHovered
          ? theme.customColors.background.hover
          : "#FFFFFF",
        cursor: "pointer",
        position: "relative",
        overflow: "visible",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          transform: "translateY(-2px)",
        },
      }}
      onClick={onClick}
    >
      <CardContent
        sx={{
          p: 2.5,
          "&:last-child": { pb: 2.5 },
          overflow: "visible",
          position: "relative",
        }}
      >
        <Box>
          {/* Top section - Logo and Title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              mb: systemData ? 2 : 0,
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                width: "56px",
                height: "56px",
                borderRadius: "10px",
                backgroundColor: theme.customColors.background.cardHeader,
                border: `1px solid ${theme.customColors.border.main}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {logoPath ? (
                <img
                  src={`${process.env.REACT_APP_POLICY_DOC_URL}/logos/${logoPath}`}
                  alt={integration?.label}
                  style={{
                    width: "40px",
                    height: "40px",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <img
                  src={getImage(integration?.key)}
                  alt={integration?.label}
                  style={{
                    width: "40px",
                    height: "40px",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>

            {/* Title */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5">
                <b>{integration?.label?.toUpperCase()}</b>
              </Typography>
            </Box>

            {/* Arrow Icon - appears on hover */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ArrowForwardIcon
                sx={{
                  fontSize: "18px",
                  color: theme.customColors.text.disabled,
                  opacity: isHovered ? 1 : 0,
                  transform: isHovered ? "translateX(0)" : "translateX(-4px)",
                  transition: "all 0.2s ease",
                }}
              />
            </Box>
          </Box>

          {/* Compliance Status Section */}
          {systemData && (
            <>
              <Divider
                sx={{
                  my: 2,
                  borderColor: theme.customColors.border.subtle,
                }}
              />
              <Box>
                {/* Labels row - side by side */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Compliance Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Checks
                  </Typography>
                </Box>

                {/* Values row - side by side */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Compliance Status */}
                  <Box
                    sx={{
                      width: "300px",
                      position: "relative",
                      zIndex: 1,
                      "&:hover": {
                        zIndex: 10000000,
                      },
                    }}
                  >
                    <ComplianceProgressBar data={complianceMetadata} />
                  </Box>

                  {/* Total Checks */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    <b>
                      <span style={{ marginRight: "8px" }}>
                        {systemData.totalChecksApplicable || 0}
                      </span>
                      Checks
                    </b>
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default InfrastructureCard;
